import { useMemo, useState } from 'react';
import type { AggregateBundle, AreaId, Filters, Meta, SourceId } from '../types';
import { monthName } from '../lib/format';

interface FilterBarProps {
  filters: Filters;
  onChange: (filters: Filters) => void;
  meta: Meta | undefined;
  stats: AggregateBundle | undefined;
  /** Alla år som finns i datan, oberoende av aktuellt filter. */
  availableYears: number[];
  /** Vanligaste orterna i hela datan, oberoende av aktuellt filter. */
  availableLocations: { name: string; count: number }[];
  allTechnologies: string[];
}

/**
 * Alla filter ligger på en rad ovanför diagrammen. Ordningen följer hur man
 * faktiskt resonerar: först när, sedan vad, sedan var ifrån.
 */
export function FilterBar({
  filters,
  onChange,
  meta,
  stats,
  availableYears,
  availableLocations,
  allTechnologies,
}: FilterBarProps) {
  const [techQuery, setTechQuery] = useState('');

  const techSuggestions = useMemo(() => {
    const query = techQuery.trim().toLowerCase();
    if (!query) return [];
    return allTechnologies
      .filter((tech) => tech.toLowerCase().includes(query) && !filters.tech.includes(tech))
      .slice(0, 8);
  }, [techQuery, allTechnologies, filters.tech]);

  const update = (patch: Partial<Filters>) => onChange({ ...filters, ...patch });

  const toggleTech = (tech: string) => {
    const next = filters.tech.includes(tech) ? filters.tech.filter((t) => t !== tech) : [...filters.tech, tech];
    update({ tech: next });
    setTechQuery('');
  };

  const hasFilters =
    filters.year !== undefined ||
    filters.month !== undefined ||
    filters.area !== undefined ||
    filters.source !== undefined ||
    filters.location !== undefined ||
    filters.seniority !== undefined ||
    filters.workMode !== undefined ||
    Boolean(filters.search) ||
    filters.datedOnly === true ||
    filters.hideRemoved === true ||
    filters.openOnly === true ||
    filters.showDuplicates === true ||
    filters.tech.length > 0;

  return (
    <section className="filters" aria-label="Filter">
      <div className="field">
        <span className="field__label">År</span>
        <div className="chip-row">
          <button
            type="button"
            className="chip"
            aria-pressed={filters.year === undefined}
            onClick={() => update({ year: undefined, month: undefined })}
          >
            Alla
          </button>
          {availableYears.map((year) => (
            <button
              key={year}
              type="button"
              className="chip"
              aria-pressed={filters.year === year}
              onClick={() => update({ year: filters.year === year ? undefined : year, month: undefined })}
            >
              {year}
            </button>
          ))}
        </div>
      </div>

      <div className="field">
        <label className="field__label" htmlFor="filter-month">
          Månad
        </label>
        <select
          id="filter-month"
          className="control"
          value={filters.month ?? ''}
          onChange={(event) => update({ month: event.target.value ? Number(event.target.value) : undefined })}
        >
          <option value="">Alla månader</option>
          {Array.from({ length: 12 }, (_, i) => i + 1).map((month) => (
            <option key={month} value={month}>
              {monthName(month)}
            </option>
          ))}
        </select>
      </div>

      <div className="field">
        <label className="field__label" htmlFor="filter-area">
          Område
        </label>
        <select
          id="filter-area"
          className="control"
          value={filters.area ?? ''}
          onChange={(event) => update({ area: (event.target.value || undefined) as AreaId | undefined })}
        >
          <option value="">Alla områden</option>
          {(meta?.areas ?? []).map((area) => (
            <option key={area.id} value={area.id}>
              {area.name}
            </option>
          ))}
        </select>
      </div>

      <div className="field" style={{ position: 'relative' }}>
        <label className="field__label" htmlFor="filter-tech">
          Teknik
        </label>
        <input
          id="filter-tech"
          className="control"
          type="search"
          placeholder="Sök teknik…"
          value={techQuery}
          autoComplete="off"
          onChange={(event) => setTechQuery(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && techSuggestions[0]) {
              event.preventDefault();
              toggleTech(techSuggestions[0]);
            }
          }}
        />
        {techSuggestions.length > 0 ? (
          <ul
            style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              zIndex: 40,
              margin: '4px 0 0',
              padding: 4,
              listStyle: 'none',
              background: 'var(--surface-2)',
              border: '1px solid var(--border-strong)',
              borderRadius: 'var(--radius-sm)',
              boxShadow: 'var(--shadow)',
              minWidth: 200,
            }}
          >
            {techSuggestions.map((tech) => (
              <li key={tech}>
                <button
                  type="button"
                  className="link-button"
                  style={{ display: 'block', width: '100%', textAlign: 'left', padding: '5px 8px', color: 'inherit' }}
                  onClick={() => toggleTech(tech)}
                >
                  {tech}
                </button>
              </li>
            ))}
          </ul>
        ) : null}
      </div>

      <div className="field">
        <label className="field__label" htmlFor="filter-source">
          Källa
        </label>
        <select
          id="filter-source"
          className="control"
          value={filters.source ?? ''}
          onChange={(event) => update({ source: (event.target.value || undefined) as SourceId | undefined })}
        >
          <option value="">Alla källor</option>
          {(stats?.sources ?? meta?.sources ?? []).map((source) => (
            <option key={source.id} value={source.id}>
              {source.name}
            </option>
          ))}
        </select>
      </div>

      <div className="field">
        <label className="field__label" htmlFor="filter-location">
          Ort
        </label>
        <select
          id="filter-location"
          className="control"
          value={filters.location ?? ''}
          onChange={(event) => update({ location: event.target.value || undefined })}
        >
          <option value="">Alla orter</option>
          {/* Närorter räknas in i sin storstad, så listan är kortare än antalet
              ortnamn i annonserna. */}
          {availableLocations.map((place) => (
            <option key={place.name} value={place.name}>
              {place.name} ({place.count})
            </option>
          ))}
        </select>
      </div>

      <div className="field">
        <label className="field__label" htmlFor="filter-seniority">
          Nivå
        </label>
        <select
          id="filter-seniority"
          className="control"
          style={{ minWidth: 120 }}
          value={filters.seniority ?? ''}
          onChange={(event) => update({ seniority: event.target.value || undefined })}
        >
          <option value="">Alla nivåer</option>
          <option value="junior">Junior</option>
          <option value="mid">Medior</option>
          <option value="senior">Senior</option>
          <option value="lead">Lead</option>
        </select>
      </div>

      <div className="field" style={{ flex: 1, minWidth: 230 }}>
        <label className="field__label" htmlFor="filter-search">
          Fritext
        </label>
        <input
          id="filter-search"
          className="control control--search"
          style={{ width: '100%' }}
          type="search"
          placeholder="Titel, kund eller ort…"
          value={filters.search ?? ''}
          onChange={(event) => update({ search: event.target.value || undefined })}
        />
      </div>

      <div className="field">
        <span className="field__label">Datering</span>
        <button
          type="button"
          className="chip"
          aria-pressed={filters.datedOnly === true}
          title="Flera källor visar bara öppna uppdrag utan publiceringsdatum. De dateras då efter när de upptäcktes, vilket gör månadsfördelningen missvisande bakåt i tiden."
          onClick={() => update({ datedOnly: filters.datedOnly ? undefined : true })}
          style={{ height: 34 }}
        >
          Endast daterade
        </button>
      </div>

      <div className="field">
        <span className="field__label">Annonser</span>
        <div className="chip-row">
          <button
            type="button"
            className="chip"
            aria-pressed={filters.hideRemoved === true}
            title="Annonser som tagits bort hos källan ligger kvar i statistiken men visas inte i listan."
            onClick={() => update({ hideRemoved: filters.hideRemoved ? undefined : true })}
            style={{ height: 34 }}
          >
            Dölj borttagna
          </button>
          <button
            type="button"
            className="chip"
            aria-pressed={filters.openOnly === true}
            title="Döljer uppdrag vars sista svarsdag passerat. Uppdrag där källan inte anger någon svarsdag räknas som öppna."
            onClick={() => update({ openOnly: filters.openOnly ? undefined : true })}
            style={{ height: 34 }}
          >
            Bara öppna
          </button>
          <button
            type="button"
            className="chip"
            aria-pressed={filters.showDuplicates === true}
            title="Samma uppdrag förmedlas ofta av flera mäklare och räknas normalt en gång. Slå på för att se varje mäklares annons för sig."
            onClick={() => update({ showDuplicates: filters.showDuplicates ? undefined : true })}
            style={{ height: 34 }}
          >
            Visa dubbletter
          </button>
        </div>
      </div>

      {hasFilters ? (
        <button type="button" className="link-button" onClick={() => onChange({ tech: [] })}>
          Rensa alla
        </button>
      ) : null}

      {filters.tech.length > 0 ? (
        <div className="chip-row" style={{ flexBasis: '100%' }}>
          {filters.tech.map((tech) => (
            <button key={tech} type="button" className="chip chip--active chip--removable" onClick={() => toggleTech(tech)}>
              {tech}
              <span className="chip__x" aria-hidden="true">
                ×
              </span>
              <span className="visually-hidden">Ta bort filter</span>
            </button>
          ))}
        </div>
      ) : null}
    </section>
  );
}
